<template>
  <div class="notifications-promotions-item">
    <div class="notifications-promotions-item__header">
      <span class="notifications-promotions-item__date">
        {{ fullDate(notification.createdAt) }}
      </span>
      <span
        v-if="!notification.hasBeenRead && notification.type !== NotificationTypes.PROMOCODE"
        class="notifications-promotions-item__button"
        @click="emit('markAsRead', props.notification.code)"
      >
        Отметить прочитанным
      </span>
    </div>

    <div :class="['notifications-promotions-item__title', classes]">
      <f-sprite-icon
        class="notifications-promotions-item__icon"
        :style="{ color: notification.hasBeenRead ? 'gray' : icon.color }"
        :icon-link="$options.icons[icon.name]"
      />
      <a
        v-if="notification.type !== NotificationTypes.ORDER"
        :href="link"
      >
        {{ notification.title }}
      </a>
      <span
        v-else
        @click="emit('openOrder', notification.url)"
      >
        {{ notification.title }}
      </span>
    </div>

    <div class="notifications-promotions-item__subtitle">
      {{ notification.text }}
    </div>
  </div>
</template>

<script lang="ts">
import bonusIcon from '@icons/promotions-widget/bonus.svg';
import messageIcon from '@icons/promotions-widget/message.svg';
import orderIcon from '@icons/promotions-widget/order.svg';
import promocodeIcon from '@icons/promotions-widget/promocode.svg';
import promotionIcon from '@icons/promotions-widget/promotion.svg';

export default {
  icons: {
    bonusIcon,
    promocodeIcon,
    orderIcon,
    messageIcon,
    promotionIcon,
  },
};
</script>

<script setup lang="ts">
import { computed } from 'vue';

import { fullDate } from '@kit/full-date';

import {
  COLORS,
  ICONS,
  NotificationTypes,
} from '@/components/app-kit/notifications-promotions/constants/notifications';

import type { TNotification } from './types/Notification';

interface IProps {
  notification: TNotification;
}

defineOptions({
  name: 'notifications-promotions-item',
});

const props = defineProps<IProps>();
const emit = defineEmits(['markAsRead', 'openOrder']);

const classes = computed(() => ({
  unread: !props.notification.hasBeenRead,
  clickable: props.notification.url || props.notification.type === NotificationTypes.PROMOCODE,
}));

const icon = computed(() => {
  switch (props.notification.type) {
    case NotificationTypes.ORDER:
      return ICONS[NotificationTypes.ORDER];
    case NotificationTypes.MESSAGE:
      return ICONS[NotificationTypes.MESSAGE];
    case NotificationTypes.BONUS:
      return ICONS[NotificationTypes.BONUS];
    case NotificationTypes.PROMOTION:
      return ICONS[NotificationTypes.PROMOTION];
    case NotificationTypes.PROMOCODE:
      return ICONS[NotificationTypes.PROMOCODE];
    default:
      return {
        color: COLORS.BLUE,
        name: ICONS[NotificationTypes.MESSAGE],
      };
  }
});

const link = computed(() => {
  switch (props.notification.type) {
    case NotificationTypes.PROMOCODE:
      return '/account/notifications';
    default:
      return props.notification.url.match(/^\//)
        ? props.notification.url
        : `/${props.notification.url}`;
  }
});
</script>

<style lang="scss">
.notifications-promotions-item {
  padding: 12px;
  box-shadow: 0 -1px 0 0 $gray_lu inset;
  background: $white;
  transition: background-color .2s ease;

  &:hover {
    background: $gray_bgr;

    .notifications-promotions-item__button {
      display: block;
    }
  }

  &__header {
    display: flex;
  }

  &__date {
    color: $gray;
  }

  &__button,
  &__title {
    line-height: 20px;
    cursor: pointer;
  }

  &__button {
    display: none;
    margin-left: auto;
    font-size: 12px;
    color: $blue;
  }

  &__title {
    display: flex;
    align-items: flex-start;
    font-size: 14px;
    pointer-events: none;

    &.unread {
      font-weight: bold;
    }

    &.clickable {
      pointer-events: all;
    }

    & > svg {
      width: 14px;
      height: 14px;
      margin-top: 2px;
      margin-right: 4px;
    }
  }

  &__title > a,
  &__subtitle {
    // stylelint-disable-next-line value-no-vendor-prefix
    display: -webkit-box;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__date,
  &__title,
  &__subtitle {
    line-height: 20px;
  }

  &__title,
  &__subtitle {
    margin-top: 4px;
  }

  &__date,
  &__subtitle {
    font-size: 12px;
    user-select: none;
  }
}
</style>
